"use client";

import { useEffect } from "react";
import { useCallback } from "react";
import { useState } from "react";
import { Capability } from "./prompt-types";
import { fetchSuggestedQueries } from "@/lib/playground-utils";
import { Spinner } from "../ui/spinner";

function Suggestion({ text, onClick, className }: { text: string; onClick: () => void; className?: string }) {
  return (
    <button
      type="button"
      className={`text-xs border-1 bg-muted border-muted-foreground rounded-md font-medium px-2 py-1 hover:cursor-pointer hover:text-foreground hover:border-foreground transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary text-left ${className || ""}`}
      onClick={onClick}
      aria-label={`Select query: ${text}`}
    >
      {text}
    </button>
  );
}

export default function SuggestedQueries({
  imageData,
  capability,
  selectSuggestedQuery,
  inferenceUrl,
}: {
  imageData: File;
  capability: Capability;
  selectSuggestedQuery: (query: string) => void;
  inferenceUrl: string;
}) {
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const [suggestedQueries, setSuggestedQueries] = useState<string[]>([]);

  // Fetch suggested queries when an image is uploaded
  const updateSuggestedQueries = useCallback(
    async (imageData: File, capability: Capability) => {
      try {
        console.log("Starting to fetch suggested queries for:", imageData.name, "capability:", capability);
        setSuggestedQueries([]);
        setSuggestionsLoading(true);
        const newSuggestedQueries = await fetchSuggestedQueries(
          inferenceUrl,
          capability,
          imageData
        );
        console.log("Received suggested queries:", newSuggestedQueries);
        setSuggestedQueries(newSuggestedQueries || []);
      } catch (err) {
        console.error("Error fetching suggested queries:", err);
      } finally {
        setSuggestionsLoading(false);
      }
    },
    [inferenceUrl]
  );

  useEffect(() => {
    if (imageData) {
      updateSuggestedQueries(imageData, capability);
    }
  }, [imageData, capability, updateSuggestedQueries]);

  return (
    <div className="space-y-2">
      {suggestionsLoading ? (
        <div className="flex flex-row gap-2">
          <Spinner className="w-4 h-4" />
          <span className="text-sm text-muted-foreground">
            Loading suggestions...
          </span>
        </div>
      ) : (
        <div className="flex flex-wrap gap-2">
          {suggestedQueries.map((question, index) => (
            <Suggestion
              key={question}
              text={question}
              onClick={() => selectSuggestedQuery(question)}
              className={index === 2 && capability === Capability.VisualQuery ? "lg:block hidden" : ""}
            />
          ))}
        </div>
      )}
    </div>
  );
}
